import { useFormik } from "formik";
import { useEffect, useState } from "react";
import MESSAGES from "../../../../common/data/constants/messages";
import { ISurvey } from "../../../../common/data/dtos/survey";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import Checks from "../../../../components/bootstrap/forms/Checks";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import surveyService from "../../../../services/survey-service";

type SurveyFormValues = {
    id?: string;
    name: string;
    isSignable: boolean,
    hasObservation: boolean,
    hasTeamLeader: boolean,
    hasInspectionResponsible: boolean,
};

interface Props {
    isModalOpened: boolean;
    selectedSurvey: ISurvey | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const SurveyModal = ({ isModalOpened, selectedSurvey, onModalClose, onAfterSubmit }: Props) => {

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const formik = useFormik({
        initialValues: {
            name: '',
            isSignable: false,
            hasObservation: false,
            hasTeamLeader: false,
            hasInspectionResponsible: false,
        },

        validate: (values) => {
            const errors: {
                name?: string;
            } = {};

            if (!values.name)
                errors.name = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    useEffect(() => {
        resetForm();

        if (selectedSurvey)
            setFieldValues(selectedSurvey);

    }, [isModalOpened]);

    const onFormSubmit = async (values: SurveyFormValues) => {
        if (selectedSurvey) {

            setIsSubmitting(true);

            const response = await surveyService.updateSurvey({
                id: selectedSurvey.id,
                name: values.name,
                isSignable: values.isSignable,
                hasObservation: values.hasObservation,
                hasTeamLeader: values.hasTeamLeader,
                hasInspectionResponsible: values.hasInspectionResponsible,
                rowVersion: selectedSurvey.rowVersion,
                status: selectedSurvey.status,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
        } else {

            setIsSubmitting(true);

            const response = await surveyService.createSurvey({
                name: values.name,
                isSignable: values.isSignable,
                hasObservation: values.hasObservation,
                hasTeamLeader: values.hasTeamLeader,
                hasInspectionResponsible: values.hasInspectionResponsible,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.SAVE_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.SAVED);
        }

        resetForm();
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                name: '',
                isSignable: false,
                hasObservation: false,
                hasTeamLeader: false,
                hasInspectionResponsible: false,
            },
        });
    };

    const setFieldValues = (values: ISurvey) => {
        formik.setFieldValue('name', values.name || '');
        formik.setFieldValue('isSignable', values.isSignable || false);
        formik.setFieldValue('hasObservation', values.hasObservation || false);
        formik.setFieldValue('hasTeamLeader', values.hasTeamLeader || false);
        formik.setFieldValue('hasInspectionResponsible', values.hasInspectionResponsible || false);
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'xl'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='survey-modal'>Manutenção de questionários</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    
                    <CardTabItem id='tab-item-1' title='Dados do questionário' icon='Feed'>
                        <div className='row g-4'>

                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='name' label='Nome' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.name}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.name}
                                        invalidFeedback={formik.errors.name}
                                        validFeedback=''
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-lg-3 col-md-3 col-sm-12'>
                                <FormGroup id='hasTeamLeader' isFloating>
                                    <Checks
                                        id="hasTeamLeader"
                                        label="Incluir responsável pela equipe"
                                        name="hasTeamLeader"
                                        checked={formik.values.hasTeamLeader}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        type="switch"
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-lg-3 col-md-3 col-sm-12'>
                                <FormGroup id='hasInspectionResponsible' isFloating>
                                    <Checks
                                        id="hasInspectionResponsible"
                                        label="Incluir responsável pela inspeção"
                                        name="hasInspectionResponsible"
                                        checked={formik.values.hasInspectionResponsible}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        type="switch"
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-lg-3 col-md-3 col-sm-12'>
                                <FormGroup id='hasObservation' isFloating>
                                    <Checks
                                        id="hasObservation"
                                        label="Incluir observações"
                                        name="hasObservation"
                                        checked={formik.values.hasObservation}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        type="switch"
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-lg-3 col-md-3 col-sm-12'>
                                <FormGroup id='isSignable' isFloating>
                                    <Checks
                                        id="isSignable"
                                        label="É assinável"
                                        name="isSignable"
                                        checked={formik.values.isSignable}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        type="switch"
                                    />
                                </FormGroup>
                            </div>

                        </div>
                    </CardTabItem>

                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};